import type { PlatformUserDto, PlatformUserListResponse } from '../../user-management/models/types';
import type { AuthUserSnapshot, ProfileRecord } from '../models/types';

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatDisplayStatus(status?: string): string {
  if (!status) return 'Inactive';
  const normalized = status.trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function isActiveStatus(status?: string): boolean {
  return String(status ?? '').trim().toUpperCase() === 'ACTIVE';
}

function readOtpEnabled(dto: Record<string, unknown>): boolean {
  const raw =
    dto.otpEnabled ??
    dto.mfaEnabled ??
    dto.twoFactorEnabled ??
    dto.isOtpEnabled;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    return normalized === 'true' || normalized === 'enabled' || normalized === 'yes';
  }
  return true;
}

export function mapProfileDto(
  dto: PlatformUserDto,
  fallback: AuthUserSnapshot & { name: string; email: string; phone: string; role: string; status: string; userType: string },
): ProfileRecord {
  const raw = dto as Record<string, unknown>;
  const created = String(dto.createdDate ?? dto.joinedDate ?? raw.createdAt ?? '');
  const updated = String(raw.updatedDate ?? raw.updatedAt ?? dto.lastLoginTime ?? created);

  return {
    id: Number(dto.userId ?? fallback.id) || fallback.id,
    userType: String(dto.userType ?? fallback.userType).toUpperCase(),
    name: String(dto.name ?? fallback.name).trim() || fallback.name,
    email: String(dto.email ?? fallback.email).trim() || fallback.email,
    phone: String(raw.phone ?? raw.phoneNumber ?? fallback.phone).trim() || '—',
    role: String(dto.role ?? fallback.role).trim() || fallback.role,
    roleId: dto.roleId != null ? Number(dto.roleId) : undefined,
    status: formatDisplayStatus(String(dto.status ?? fallback.status)),
    otpEnabled: readOtpEnabled(raw),
    createdAt: created,
    updatedAt: updated,
  };
}

export function buildFallbackProfile(user: AuthUserSnapshot): ProfileRecord {
  return {
    id: user.id,
    userType: user.userType ?? 'ADMIN',
    name: user.fullName?.trim() || '—',
    email: user.email?.trim() || '—',
    phone: user.phone?.trim() || '—',
    role: user.adminType || user.role || 'admin',
    status: formatDisplayStatus(user.status || 'ACTIVE'),
    otpEnabled: true,
    createdAt: '',
    updatedAt: '',
  };
}

export function normalizeProfileUsers(raw: unknown): PlatformUserDto[] {
  const data = (raw ?? {}) as PlatformUserListResponse;
  if (Array.isArray(data.content) && data.content.length > 0) return data.content;
  return [
    ...(Array.isArray(data.admins) ? data.admins : []),
    ...(Array.isArray(data.merchants) ? data.merchants : []),
  ];
}
